import { Box, Button } from '@mui/material'
import { Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import CompanyViewForm from './CompanyViewForm'
import { CompanyForm, CompanyModel } from '../models/company.model'
import { useCompanies } from '../hooks/use-companies'
import { isUndefined } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { CompanyContactData } from 'src/Libs/user/models'
import useModals from 'src/Hooks/useModals'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { CompanyConnectedModel } from '../models'
import { useCompanyFormInitializer } from '../hooks'
import { useCountryCodesValidation } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { modelsToForm } from '../transformers/models-to-form'

const useStyles = makeStyles()(theme => ({
  companyContainer: {
    width: '100%',
    padding: `0 ${theme.spacing(2)}`,
  },
}))

interface Props {
  company?: CompanyModel
  companyLocation?: CompanyContactData
  refetchCompany?: () => void
}

/**
 *  This Component displays the Form to edit a company
 */
const CompanyDataView: React.FC<Props> = ({ company, companyLocation, refetchCompany }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { initialValues, validationSchema } = useCompanyFormInitializer(company, companyLocation)
  const [formValues, setFormValues] = useState<CompanyForm>(initialValues)
  const { createCompany, updateCompany, getCompaniesConnected } = useCompanies()
  const navigate = useNavigate()
  const { openConfirmationModal } = useModals()
  const [validateCountryCode] = useCountryCodesValidation()
  const country = companyLocation?.Land ?? ''
  const validatedCountryCode = validateCountryCode(country) ? country : 'DEU'

  useEffect(() => {
    if (isUndefined(company)) return
    setFormValues(modelsToForm(validatedCountryCode, company, companyLocation))
  }, [company, companyLocation]) /* eslint-disable-line */

  const onSaveCompany = (values: CompanyForm): void => {
    const { shopPermission, ...basePayload } = values
    const payload = basePayload

    if (values.sapCustomerId) {
      Object.assign(payload, { shopPermission: shopPermission })
    }

    if (company?.id) {
      updateCompany(company.id, payload).then(() => refetchCompany?.())
    } else {
      createCompany(payload).then(id => {
        if (id) {
          navigate(`/overview/companies/${id}`, { replace: true })
        }
      })
    }
  }

  const onShowCompany = (duplicateCompanyId: number, onClose: () => void): void => {
    navigate(`/overview/companies/${duplicateCompanyId}`)
    onClose()
  }

  const openDialog = (values: CompanyForm, duplicates: CompanyConnectedModel[]): void => {
    openConfirmationModal({
      buttonText: [t('common.cancel'), t('common.save')],
      onConfirm: onClose => {
        onSaveCompany(values)
        onClose()
      },
      text: t('service-dashboard.company.duplicate-found.text'),
      title: t('service-dashboard.company.duplicate-found.title'),
      renderAdditionalButtonContent: onClose => {
        return (
          <Button onClick={() => onShowCompany(duplicates[0].companyId, onClose)}>
            {t('service-dashboard.company.duplicate-found.show-duplicate')}
          </Button>
        )
      },
    })
  }

  const onSubmitCompanyForm = (values: CompanyForm): void => {
    getCompaniesConnected({ filter: `firma.Name=${values.name},location.PLZ=${values.zipCode},location.Address=${values.city}` }).then(data => {
      const hasNameOrAddressChanged =
        values.name !== formValues.name || values.zipCode !== formValues.zipCode || values.address !== formValues.address
      const duplicates = data.filter(c => c.companyId !== company?.id)

      if (duplicates.length > 0 && hasNameOrAddressChanged) {
        openDialog(values, duplicates)
      } else {
        onSaveCompany(values)
      }
    })
  }

  return (
    <Box className={classes.companyContainer}>
      <Formik initialValues={formValues} onSubmit={onSubmitCompanyForm} enableReinitialize validationSchema={validationSchema}>
        <CompanyViewForm companyId={company?.id} />
      </Formik>
    </Box>
  )
}

export default CompanyDataView
